import { useContext } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { StableNavigateContext } from './context'

/**
 * Returns a referentially-stable `navigate` function backed by a `useRef`
 * that the provider keeps current as React Router's own navigate identity
 * changes. Unlike `useNavigate()` from react-router-dom — which returns a
 * fresh function per render and breaks `useEffect` / `useCallback` /
 * `useMemo` dependency arrays — this hook's return value is referentially
 * stable across the component lifetime.
 *
 * **When to use which navigate hook:**
 * - {@link useStableNavigate} — base layer, no offline check. Pick this
 *   inside shared utilities that should NOT trigger the offline modal
 *   (e.g. `useMultipleQueries` redirect-on-401, internal route helpers).
 *   Also fine in app code if you have a deliberate reason to bypass the
 *   offline gate.
 * - `useSafeNavigate` — wraps this hook + adds offline-awareness: if
 *   `navigator.onLine` is false, it dispatches a custom event to surface
 *   the global OfflineModal and queues a retry callback. **Default choice
 *   for app code (features, widgets, pages).**
 *
 * Both hooks throw if called outside `StableNavigateContextProvider`
 * (mounted at the app root, see `src/main.tsx`).
 */
export function useStableNavigate(): NavigateFunction {
  const navigateRef = useContext(StableNavigateContext)
  if (!navigateRef) {
    throw new Error('StableNavigate context is not initialized')
  }
  return navigateRef.current
}
